// src/components/editors/ColorPicker.js
import React from 'react';

/**
 * ColorPicker - Reusable color picker component with hex input
 *
 * @param {string} value - Current hex color (e.g., "#7C3AED")
 * @param {function} onChange - Callback when color changes
 * @param {string} label - Label text for the picker
 * @param {string} helperText - Optional helper text below picker
 * @param {boolean} showHexInput - Show hex text input (default: true)
 */
const ColorPicker = ({
  value,
  onChange,
  label,
  helperText,
  showHexInput = true
}) => {
  const handleColorChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Color preview swatch */}
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            border: '2px solid #E5E7EB',
            backgroundColor: value || '#000000',
            flexShrink: 0
          }}
        />

        {/* Color picker trigger */}
        <label className="relative cursor-pointer" style={{ flexShrink: 0 }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            border: '2px solid #D1D5DB',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.15s'
          }}>
            <div style={{ position: 'relative', width: '20px', height: '20px' }}>
              {/* Color wheel visual */}
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)'
                }}
              />
              {/* Pencil icon in center */}
              <div style={{
                position: 'absolute',
                inset: '4px',
                background: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg style={{ width: '10px', height: '10px', color: '#6B7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
            </div>
          </div>
          <input
            type="color"
            value={value || '#000000'}
            onChange={handleColorChange}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: 'pointer'
            }}
          />
        </label>

        {/* Hex input */}
        {showHexInput && (
          <input
            type="text"
            value={value || '#000000'}
            onChange={handleColorChange}
            placeholder="#000000"
            style={{
              flex: 'none',
              width: '100px',
              maxWidth: '100px',
              padding: '6px 8px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontFamily: 'monospace',
              fontSize: '12px',
              textTransform: 'uppercase',
              boxSizing: 'border-box'
            }}
          />
        )}
      </div>

      {helperText && (
        <p className="text-xs text-gray-500 mt-2">{helperText}</p>
      )}
    </div>
  );
};

export default ColorPicker;
